import { effectiveCatalogLifecycle, findRuntimeCoefficient } from '../../data/economicContribution/sourceCatalog';
import {
  CONSTRUCTION_JOBS_MW_RANGE_MODEL_VERSION,
  CONSTRUCTION_JOBS_MODEL_VERSION,
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type ConstructionJobsInput,
  type ConstructionJobsMwRangeScreeningResult,
  type ConstructionJobsResult,
  type EconomicSourceCatalogSnapshot,
  type MwRangeQuantity,
} from '../../types/economicContribution';
import { bandForMw, finiteNonNegative, finitePositive, hashEconomicModelInput, roundEconomic } from './modelUtils';
import { staffingBasisFactor, staffingBasisLimitation } from './powerBasisDisclosure';

function unavailable(
  input: ConstructionJobsInput,
  catalog: EconomicSourceCatalogSnapshot,
  reason: NonNullable<ConstructionJobsResult['unavailableReason']>,
  limitations: readonly string[],
): ConstructionJobsResult {
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: CONSTRUCTION_JOBS_MODEL_VERSION,
    status: 'not-calculated',
    siteId: input.siteId,
    peakOnsiteWorkers: null,
    averageOnsiteWorkers: null,
    directJobYears: null,
    durationMonths: null,
    evidenceBasis: null,
    phaseBasis: input.load.phaseBasis,
    unavailableReason: reason,
    modelInputHash: hashEconomicModelInput({ input, catalogVersion: catalog.catalogVersion }),
    sourceCatalogVersion: catalog.catalogVersion,
    sourceEntryIds: [],
    trace: [],
    limitations,
  };
}

export function calculateConstructionJobs(
  input: ConstructionJobsInput,
  catalog: EconomicSourceCatalogSnapshot,
  asOf: Date = new Date(),
): ConstructionJobsResult {
  const inputHash = hashEconomicModelInput({ input, catalogVersion: catalog.catalogVersion });
  if (!input.siteId.trim() || !finitePositive(input.load.value)) {
    return unavailable(input, catalog, 'invalid-input', ['Site id and a finite positive MW value are required.']);
  }

  if (input.operatorProvided) {
    const provided = input.operatorProvided;
    const validPeak = provided.peakOnsiteWorkers == null || finiteNonNegative(provided.peakOnsiteWorkers);
    const validAverage = provided.averageOnsiteWorkers == null || finiteNonNegative(provided.averageOnsiteWorkers);
    const validJobYears = provided.directJobYears == null || finiteNonNegative(provided.directJobYears);
    const validDuration = provided.durationMonths == null || finitePositive(provided.durationMonths);
    const hasValue = provided.peakOnsiteWorkers != null
      || provided.averageOnsiteWorkers != null
      || provided.directJobYears != null;
    if (!provided.sourceLabel.trim() || !validPeak || !validAverage || !validJobYears || !validDuration || !hasValue) {
      return unavailable(input, catalog, 'operator-input-incomplete', [
        'Operator construction data requires a source and at least one valid workforce quantity.',
      ]);
    }
    const pointRange = (value: number | undefined) => value == null ? null : { low: value, high: value };
    return {
      schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
      modelVersion: CONSTRUCTION_JOBS_MODEL_VERSION,
      status: 'operator-provided',
      siteId: input.siteId,
      peakOnsiteWorkers: pointRange(provided.peakOnsiteWorkers),
      averageOnsiteWorkers: pointRange(provided.averageOnsiteWorkers),
      directJobYears: pointRange(provided.directJobYears),
      durationMonths: provided.durationMonths ?? input.constructionDurationMonths ?? null,
      evidenceBasis: 'operator-provided',
      phaseBasis: input.load.phaseBasis,
      modelInputHash: inputHash,
      sourceCatalogVersion: catalog.catalogVersion,
      sourceEntryIds: [],
      trace: [{
        id: 'operator-provided-construction',
        expression: 'display operator/EPC quantities without model substitution',
        inputs: {
          peakOnsiteWorkers: provided.peakOnsiteWorkers ?? null,
          averageOnsiteWorkers: provided.averageOnsiteWorkers ?? null,
          directJobYears: provided.directJobYears ?? null,
          durationMonths: provided.durationMonths ?? null,
          sourceLabel: provided.sourceLabel,
        },
        output: 'operator-provided',
        sourceEntryIds: [],
      }],
      limitations: ['Operator/EPC quantities are displayed as supplied and are not independently verified by the platform.'],
    };
  }

  if (input.load.basis !== 'utility-service') {
    return unavailable(input, catalog, 'unsupported-mw-basis', [
      `Construction coefficients require utility-service MW; supplied basis is ${input.load.basis}.`,
    ]);
  }

  const intensityEntry = findRuntimeCoefficient(catalog, 'size-band-intensity', {
    use: 'construction-peak-workers-per-mw',
    permittedUse: 'construction-jobs-screening:peak-workers',
    asOf,
  });
  const ratioEntry = findRuntimeCoefficient(catalog, 'construction-average-to-peak-ratio', {
    permittedUse: 'construction-jobs-screening:average-workers', asOf,
  });
  const durationEntry = findRuntimeCoefficient(catalog, 'construction-duration-years', {
    permittedUse: 'construction-jobs-screening:duration', asOf,
  });
  if (!intensityEntry) {
    return unavailable(input, catalog, 'approved-source-unavailable', [
      'A policy-valid peak-intensity entry is required.',
    ]);
  }
  const band = bandForMw(intensityEntry.coefficient.bands, input.load.value);
  if (!band) {
    return unavailable(input, catalog, 'outside-approved-domain', [
      `${input.load.value} MW is outside the policy-valid construction size bands; no interpolation is applied.`,
    ]);
  }

  const operatorDurationMonths = finitePositive(input.constructionDurationMonths)
    ? input.constructionDurationMonths
    : null;
  const durationMonths = operatorDurationMonths ?? (durationEntry
    ? { low: durationEntry.coefficient.low * 12, high: durationEntry.coefficient.high * 12 }
    : null);
  const durationYears = operatorDurationMonths != null
    ? { low: operatorDurationMonths / 12, high: operatorDurationMonths / 12 }
    : durationEntry
      ? { low: durationEntry.coefficient.low, high: durationEntry.coefficient.high }
      : null;
  const rawPeakLow = input.load.value * band.low;
  const rawPeakHigh = input.load.value * band.high;
  const rawAverageLow = ratioEntry ? rawPeakLow * ratioEntry.coefficient.low : null;
  const rawAverageHigh = ratioEntry ? rawPeakHigh * ratioEntry.coefficient.high : null;
  const peakLow = roundEconomic(rawPeakLow, 1);
  const peakHigh = roundEconomic(rawPeakHigh, 1);
  const averageLow = rawAverageLow == null ? null : roundEconomic(rawAverageLow, 1);
  const averageHigh = rawAverageHigh == null ? null : roundEconomic(rawAverageHigh, 1);
  const jobYearsLow = rawAverageLow == null || durationYears == null ? null : roundEconomic(rawAverageLow * durationYears.low, 1);
  const jobYearsHigh = rawAverageHigh == null || durationYears == null ? null : roundEconomic(rawAverageHigh * durationYears.high, 1);
  const sourceEntryIds = [
    intensityEntry.id,
    ...(ratioEntry ? [ratioEntry.id] : []),
    ...(durationEntry && !input.constructionDurationMonths ? [durationEntry.id] : []),
  ];
  const basisFactor = staffingBasisFactor(input.load.basisTreatment);
  const basisLimitation = staffingBasisLimitation(input.load, 'construction');

  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: CONSTRUCTION_JOBS_MODEL_VERSION,
    status: 'calculated',
    siteId: input.siteId,
    peakOnsiteWorkers: { low: peakLow, high: peakHigh },
    averageOnsiteWorkers: averageLow == null || averageHigh == null ? null : { low: averageLow, high: averageHigh },
    directJobYears: jobYearsLow == null || jobYearsHigh == null ? null : { low: jobYearsLow, high: jobYearsHigh },
    durationMonths,
    evidenceBasis: 'modelled',
    phaseBasis: input.load.phaseBasis,
    modelInputHash: inputHash,
    sourceCatalogVersion: catalog.catalogVersion,
    sourceEntryIds,
    trace: [
      {
        id: 'peak-workers',
        expression: 'utility-service MW × size-band construction workers per MW',
        inputs: { mw: input.load.value, band: band.id, lowPerMw: band.low, highPerMw: band.high },
        output: `${peakLow}–${peakHigh} peak workers`,
        sourceEntryIds: [intensityEntry.id],
      },
      ...(ratioEntry && averageLow != null && averageHigh != null ? [{
        id: 'average-workers',
        expression: 'peak worker range × average-to-peak ratio range',
        inputs: { peakLow, peakHigh, ratioLow: ratioEntry.coefficient.low, ratioHigh: ratioEntry.coefficient.high },
        output: `${averageLow}–${averageHigh} average workers`,
        sourceEntryIds: [ratioEntry.id],
      }] : []),
      ...(jobYearsLow != null && jobYearsHigh != null && durationYears != null ? [{
        id: 'direct-job-years',
        expression: 'average worker range × construction duration range',
        inputs: { averageLow, averageHigh, durationYearsLow: durationYears.low, durationYearsHigh: durationYears.high },
        output: `${jobYearsLow}–${jobYearsHigh} direct job-years`,
        sourceEntryIds: durationEntry && !input.constructionDurationMonths ? [durationEntry.id] : [],
      }] : []),
    ],
    limitations: [
      ...(basisFactor ? [basisFactor] : []),
      ...(basisLimitation ? [basisLimitation] : []),
      ...intensityEntry.limitations,
      ...(ratioEntry?.limitations ?? ['Average workforce and job-years are withheld because the average-to-peak coefficient lacks two independent sources.']),
      ...(durationEntry?.limitations ?? []),
      'Peak workers, average workers, and job-years are distinct quantities and must not be combined.',
      'Direct onsite construction only; excludes indirect and induced employment.',
      ...[intensityEntry, ratioEntry, durationEntry]
        .filter((entry): entry is NonNullable<typeof entry> => entry != null)
        .filter((entry) => effectiveCatalogLifecycle(entry, asOf) === 'approved-stale')
        .map((entry) => `Approved source entry ${entry.id} is past its next review date and is labeled stale pending refresh.`),
    ],
  };
}

/** Screen the recorded low and high MW endpoints without inventing a midpoint. */
export function calculateConstructionJobsMwRange(
  input: ConstructionJobsInput,
  loadRange: MwRangeQuantity,
  catalog: EconomicSourceCatalogSnapshot,
  asOf: Date = new Date(),
): ConstructionJobsMwRangeScreeningResult {
  const validRange = finitePositive(loadRange.low)
    && finitePositive(loadRange.high)
    && loadRange.high >= loadRange.low;
  const endpointInput = (value: number): ConstructionJobsInput => ({
    ...input,
    load: {
      value,
      basis: loadRange.basis,
      phaseBasis: loadRange.phaseBasis,
      ...(loadRange.sourceBasis ? { sourceBasis: loadRange.sourceBasis } : {}),
      basisTreatment: loadRange.basisTreatment,
      ...(loadRange.basisSource ? { basisSource: loadRange.basisSource } : {}),
      ...(loadRange.basisConversionPue ? { basisConversionPue: loadRange.basisConversionPue } : {}),
    },
  });
  const lowEndpoint = calculateConstructionJobs(endpointInput(loadRange.low), catalog, asOf);
  const highEndpoint = calculateConstructionJobs(
    endpointInput(validRange ? loadRange.high : Number.NaN),
    catalog,
    asOf,
  );
  const calculated = (result: ConstructionJobsResult): boolean => result.status !== 'not-calculated'
    && (result.peakOnsiteWorkers != null || result.averageOnsiteWorkers != null || result.directJobYears != null);
  const calculatedCount = Number(calculated(lowEndpoint)) + Number(calculated(highEndpoint));
  const sourceEntryIds = [...new Set([
    ...lowEndpoint.sourceEntryIds,
    ...highEndpoint.sourceEntryIds,
  ])];
  const limitations = [
    'The low and high MW endpoints are screened independently; no midpoint is presented as a recorded site fact.',
    'Endpoint results are not an interpolated range and may use different policy-valid construction coefficient bands.',
    ...(staffingBasisLimitation(loadRange, 'construction')
      ? [staffingBasisLimitation(loadRange, 'construction')!]
      : []),
  ];
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: CONSTRUCTION_JOBS_MW_RANGE_MODEL_VERSION,
    status: calculatedCount === 2
      ? 'calculated'
      : calculatedCount === 1 ? 'partially-calculated' : 'not-calculated',
    siteId: input.siteId,
    loadRange,
    lowEndpoint,
    highEndpoint,
    sourceEntryIds,
    limitations,
    modelInputHash: hashEconomicModelInput({ input, loadRange, catalogVersion: catalog.catalogVersion }),
    sourceCatalogVersion: catalog.catalogVersion,
  };
}
